import { Site } from '@ps/web-core-enums';
import { FeatureFlag, License } from '@ps/web-core-store';
import type { Account, LocalSettingsState, Modal } from '@ps/web-core-store';
import { get } from 'lodash';

import { NotificationType } from '@swf/components/StarsMPNotification/enums';
import { getTransformedInputData as transformMPNotification } from '@swf/components/StarsMPNotification/utils';
import { call } from '@swf/core/api/embedded';
import { SuspendAccountCode } from '@swf/core/enums';
import type {
  BaseMPNotification,
  DisplayValuePropositionNotification,
  ForceLogout,
  GeoComplyPLC,
  InactivityMessage,
  InactivityNotification,
  LobbyNotification,
  LoginEventNotification,
  Notification,
  NotificationMessage,
  RawNotification,
  SeUpdateNotification,
  SuspendAccount
} from '@swf/core/types/Notification';
import { LoginEventType, NotificationCode, TokenInvalidReason } from '@swf/core/types/Notification';

interface Props {
  accountUpdate?(props: Partial<Account>): void;
  addCleared?(props: Notification): void;
  addExpiring?(props: Notification): void;
  addIssued?(props: Notification): void;
  body: NotificationMessage['body'];
  currentModal: string;
  features: Partial<{ [key in FeatureFlag]: boolean }>;
  geoComplyPLC(props: GeoComplyPLC): void;
  getClearedById?(ticketRollId: number): Notification;
  getExpiringById?(ticketRollId: number | string): Notification;
  getIsNotificationEnabled?(notification: BaseMPNotification): Promise<boolean>;
  getIssuedById?(ticketRollId: number | string): Notification;
  getRemainingTime?(): Promise<number>;
  isEmbedded?: boolean;
  isSiteUS: boolean;
  license: License;
  locale: string;
  localSettingsSet?(props: LocalSettingsState): void;
  logout(props?: { sessionExpired: boolean } | object): void;
  modal: {
    close(path: string): void;
    enqueue(props: Modal): void;
    replace(props: Modal[]): void;
    unique(props: Modal): void;
    unshift(props: Modal): void;
    unshiftUnique(props: Modal): void;
  };
  remainingInactivitySeconds?: number;
  showVegasContent?: boolean;
  site: Site;
  type: string;
}

const geoComplyVerified = 1;

const parseNotification = (rawNotification: RawNotification): Notification => {
  if (rawNotification) {
    const { clearbets, rollid, notificationid } = rawNotification;
    return {
      ...rawNotification,
      ...(clearbets ? { clearBets: clearbets } : {}),
      ...(rollid ? { rollId: rollid } : {}),
      ...(notificationid ? { notificationId: notificationid } : {})
    };
  }
  return rawNotification;
};

export const handleNotifications = async ({
  accountUpdate,
  addCleared,
  addExpiring,
  addIssued,
  body,
  currentModal,
  features,
  geoComplyPLC,
  getClearedById,
  getExpiringById,
  getIsNotificationEnabled,
  getIssuedById,
  getRemainingTime,
  isEmbedded,
  isSiteUS,
  license,
  locale,
  localSettingsSet,
  logout,
  modal,
  remainingInactivitySeconds,
  showVegasContent,
  site,
  type
}: Props) => {
  const notification = parseNotification(body.notification as RawNotification);
  const ticketRollId = `gameTicket-${get(notification, ['gameTicketId'], '')}`;
  const newGameTicketEnabled = features[FeatureFlag.WebNotificationEnabled];
  const webMPCNotificationEnabled = features[FeatureFlag.WebMPCNotificationEnabled];

  switch (type) {
    case 'gameticket.expiring':
      if (!isEmbedded && !getExpiringById?.(ticketRollId)) {
        addExpiring?.({ rollId: ticketRollId } as Notification);
        modal.enqueue({
          path: '/notifications/game-ticket/expiring',
          locationState: { ...notification, ticketRollId }
        });
      }
      break;
    case 'gameticket.issued':
      if (!isEmbedded && !getIssuedById?.(ticketRollId) && !newGameTicketEnabled) {
        addIssued?.({ rollId: ticketRollId } as Notification);
        modal.enqueue({
          path: '/notifications/game-ticket/issued',
          locationState: { ...notification, ticketRollId }
        });
      }
      break;
    case 'multipurpose.notification': {
      const notificationData = notification as BaseMPNotification;
      const isNotificationEnabled = await getIsNotificationEnabled?.(notificationData);

      if (!isEmbedded && isNotificationEnabled) {
        modal.enqueue({
          path: '/mpnotifications',
          locationState: transformMPNotification(notificationData, locale)
        });
      }
      break;
    }
    case 'notification.tokenExpireWarning': {
      if (currentModal?.includes('/eula')) {
        break;
      }
      const { remainingSeconds } = notification as InactivityNotification;

      if (remainingInactivitySeconds && remainingSeconds) {
        setTimeout(async () => {
          const remainingTime = await getRemainingTime?.();
          if (remainingTime && remainingTime <= remainingInactivitySeconds) {
            modal.unshift?.({
              path: '/notifications/inactivity-timeout-warning',
              locationState: {
                ...notification,
                remainingSeconds: remainingInactivitySeconds
              }
            });
          }
        }, (remainingSeconds - remainingInactivitySeconds) * 1000);
        break;
      } else {
        const path = [
          License.CAON,
          License.EU,
          License.COM,
          License.GR,
          License.USMI,
          License.USPA
        ].includes(license as License)
          ? '/notifications/inactivity-timeout-warning'
          : '/notifications/inactivity-warning';

        modal.unshift?.({ path, locationState: notification });
        break;
      }
    }
    case 'session.logout.reply': {
      const { reasonCode } = body as InactivityMessage;
      if (
        !(
          currentModal === '/account/migration' && reasonCode === TokenInvalidReason.EXPLICIT_LOGOUT
        )
      ) {
        await logout?.({ sessionExpired: true });
      }
      if (isEmbedded) {
        call(['auth', 'login']);
        break;
      }
      switch (reasonCode) {
        case TokenInvalidReason.PANIC_BUTTON_FORCE_LOGOUT:
        case TokenInvalidReason.EXPLICIT_LOGOUT:
        case TokenInvalidReason.PLAYING_TIME_REACHED:
          // do nothing
          break;
        case TokenInvalidReason.NOT_ALLOWED_TO_PLAY:
          modal.enqueue({
            path: '/notifications/play-session-limit-gr',
            locationState: { msg: (body as InactivityMessage).text }
          });
          break;
        case TokenInvalidReason.GAMSTOP_FORCE_LOGOUT:
          modal.enqueue({ path: '/notifications/gamstop-exclusion' });
          break;
        case TokenInvalidReason.EXPIRE:
          modal.enqueue({
            path: '/notifications/inactivity-logout',
            locationState: notification || {
              idleSeconds: (body as InactivityMessage).idleSeconds
            }
          });
          break;
        case TokenInvalidReason.SELF_EXCLUSION_FORCE_LOGOUT:
          modal.enqueue({ path: '/notifications/account-self-excluded' });
          break;
        case TokenInvalidReason.PERMANENT_SELF_EXCLUSION_FORCE_LOGOUT:
          modal.enqueue({ path: '/notifications/permanent-self-excluded-uk' });
          break;
        case TokenInvalidReason.ADMIN_FORCE_LOGOUT:
        default:
          modal.enqueue({ path: '/notifications/force-logout' });
      }
      break;
    }
    case 'notification.account.info':
      accountUpdate?.(body.notification as Account);
      break;
    case 'mpc.issued': {
      if (!webMPCNotificationEnabled) {
        modal.enqueue({ path: '/notifications/chest', locationState: notification });
      }
      break;
    }
    case 'cvl.state.notification':
      modal.enqueue({ path: '/notifications/chest-tier', locationState: notification });
      break;
    case 'freespin.expiring':
      if (!isEmbedded && !getExpiringById?.(+notification.rollId)) {
        addExpiring?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.FreeSpinsExpiring
          }
        });
      }
      break;
    case 'freespin.issued':
      if (!isEmbedded && !getIssuedById?.(+notification.rollId)) {
        addIssued?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.FreeSpinsIssued
          }
        });
      }
      break;
    case 'userroll.issued':
      if (!isEmbedded && !getIssuedById?.(+notification.rollId)) {
        addIssued?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.BonusIssued
          }
        });
      }
      break;
    case 'userroll.expiring':
      if (!isEmbedded && !getExpiringById?.(+notification.rollId)) {
        addExpiring?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.BonusExpiring
          }
        });
      }
      break;
    case 'userroll.cleared':
      if (!isEmbedded && !getClearedById?.(+notification.rollId)) {
        addCleared?.(notification);
        modal.enqueue({
          path: '/mpnotifications',
          locationState: {
            ...notification,
            type: NotificationType.BonusCleared
          }
        });
      }
      break;
    case 'notification.session.duration':
      // @TODO when RO license is on the web check why WMT sends this message before set time
      if (!isEmbedded) {
        modal.enqueue({
          path: '/notifications/session-duration',
          locationState: notification
        });
      }
      break;
    case 'notification.password.change':
      modal.enqueue({
        path: '/notifications/password-change',
        locationState: notification
      });
      break;
    case 'notification.lobby.msg':
      if (!isEmbedded) {
        const lobbyNotification = body.notification as LobbyNotification;

        switch (true) {
          case showVegasContent &&
            lobbyNotification.notificationCode === NotificationCode.SessionInfo:
            modal.close?.('/notifications/hourly-notification-de');
            modal.unshift?.({
              path: '/notifications/hourly-notification-de',
              locationState: notification
            });
            break;
          case site === Site.GR:
            localSettingsSet?.({ playSessionLimitGR: notification });
            modal.unshift?.({
              path: '/notifications/play-session-limit-gr',
              locationState: notification
            });
            break;
          case [Site.SE, Site.NL].includes(site as Site):
            modal.close?.('/notifications/logout-restrictions');
            modal.unshift?.({
              path: '/notifications/logout-restrictions',
              locationState: notification
            });
            break;
          case isSiteUS:
            modal.close('/notifications/restrict-playing-time');
            localSettingsSet?.({ restrictPlayingTime: notification });
            modal.unshift({
              path: '/notifications/restrict-playing-time',
              locationState: notification
            });
            break;
          case site === Site.ES &&
            [NotificationCode.SessionFinalize, NotificationCode.TimeExpireWarning].includes(
              lobbyNotification.notificationCode
            ):
            modal.unique({
              path: '/notifications/es-gaming-limit-messages',
              locationState: notification
            });
            break;
          default:
            modal.enqueue({
              path: '/notifications/simple-notification',
              locationState: notification
            });
            break;
        }
      }
      break;
    case 'notification.session.summary':
      if (!isEmbedded) {
        if (currentModal === '/notifications/session-summary-notification') {
          modal.close('/notifications/session-summary-notification');
        }

        const { notificationId } = body as RawNotification;

        switch (site) {
          case Site.ES:
            modal.unique({
              path: '/notifications/es-reality-check',
              locationState: notification
            });
            break;
          default:
            localSettingsSet?.({ sessionSummary: { ...notification, notificationId } });
            modal.unshift({
              path: '/notifications/session-summary-notification',
              locationState: { ...notification, notificationId }
            });
            break;
        }
      }
      break;
    case 'notification.login.event': {
      const loginNotification = body.notification as LoginEventNotification;
      const ITLicense = license === License.IT;

      switch (loginNotification.event) {
        case ITLicense && LoginEventType.ValidateAccountReminder:
        case ITLicense && LoginEventType.SuspendedDocumentsSubmission:
        case ITLicense && LoginEventType.AccountVerification:
          modal.unique({
            path: '/notifications/documents-submission',
            locationState: { ...loginNotification }
          });
          break;
        case LoginEventType.ValidateAccountReminder:
          if (license === License.DK) {
            modal.unique({
              path: '/notifications/validate-account-reminder',
              locationState: loginNotification
            });
          } else if ([License.RO, License.CZ].includes(license as License)) {
            localSettingsSet?.({ validateAccountDays: loginNotification.param });
            modal.unique({
              path: '/notifications/validate-account-reminder',
              locationState: loginNotification
            });
          }
          break;
        case LoginEventType.AccountVerification:
          modal.unique({ path: '/notifications/account-verification' });
          break;
      }
      break;
    }
    case 'notification.displayValueProposition':
      if (!isEmbedded) {
        const { selfExclusionExpirationDate } = notification as DisplayValuePropositionNotification;

        if (selfExclusionExpirationDate) {
          accountUpdate?.({ selfExclusionExpirationDate });
        }
        modal.unique({
          path: '/notifications/value-proposition/before-you-begin',
          locationState: { openDeposit: site === Site.UK }
        });
      }
      break;
    case 'notification.player.blacklisted': {
      const { code } = body.notification as SuspendAccount;
      switch (code) {
        case SuspendAccountCode.Blacklisted:
          modal.replace([
            {
              path: '/notifications/suspended-account',
              locationState: { code }
            }
          ]);
          break;
        case SuspendAccountCode.Suspended:
        case SuspendAccountCode.ExclusionList:
        case SuspendAccountCode.PlayerNotAuthorized:
          modal.enqueue({
            path: '/notifications/suspended-account',
            locationState: { code }
          });
          break;
      }
      break;
    }
    case 'notification.geocomply.locate': {
      geoComplyPLC?.(body.notification as GeoComplyPLC);
      break;
    }
    case 'notification.geoip.result':
      if ((body.notification as GeoComplyPLC).result !== geoComplyVerified) {
        modal.enqueue({ path: '/notifications/geo-comply', locationState: notification });
      }
      break;
    case 'notification.session.forcelogout': {
      await logout?.({});
      if (site === Site.GR) {
        localSettingsSet?.({ playSessionLimitGR: notification });
        modal.enqueue({
          path: '/notifications/play-session-limit-gr',
          locationState: notification
        });
      } else if (site === Site.CZ) {
        const { reasonCode } = body.notification as ForceLogout;
        if (reasonCode !== TokenInvalidReason.SELF_EXCLUSION_FORCE_LOGOUT)
          modal.enqueue({
            path: '/notifications/admin-info-notification',
            locationState: notification
          });
      }
      break;
    }
    case 'notification.admin.info': {
      modal.enqueue({
        path: '/notifications/admin-info-notification',
        locationState: notification
      });
      break;
    }
    case 'notification.lugas.long.session':
      if (!isEmbedded) {
        modal.unshiftUnique({
          path: '/notifications/playing-timeout-break',
          locationState: notification
        });
      }
      break;
    case 'notification.se.change': {
      const { selfExclusionEndDate } = notification as SeUpdateNotification;
      if (new Date(Number(selfExclusionEndDate)) < new Date()) {
        modal.replace([{ path: '/notifications/expired-self-excluded-popup' }]);
      } else {
        modal.replace([
          {
            path: '/notifications/self-excluded-popup',
            locationState: { selfExclusionEndTime: selfExclusionEndDate }
          }
        ]);
      }
      break;
    }
    case 'notification.account.reinstate':
      modal.enqueue({
        path: '/notifications/reinstate-account-initial-es'
      });
      break;
  }
};
